import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  IonContent,
  IonPage,
  IonList,
  IonText,
  IonBadge,
  IonSpinner,
  IonButton,
  IonIcon,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonTitle,
  IonToolbar,
  IonItem,
  IonLabel,
  IonInput,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
  IonSelect,
  IonSelectOption,
  useIonToast
} from "@ionic/react";
import Header from "src/components/Header";
import { useHistory } from "react-router-dom";
import {
  fetchMisPedidos,
  fetchCabeceraDetalle,
  fetchPuntoServicio,
  fetchEstadosPedido,
  IfetchPuntoServicio,
  IEstadoPedido,
} from "src/services/PedidoDeProducto/pedido.producto.services";
import LoginAPI from "src/services/login";
import { cargoParaPedido } from "src/config/cargoPedido";
import { eye, search, closeCircle, chevronBack, chevronForward } from "ionicons/icons";
import moment from "moment";

const FECHA_CORTE_TRAZABILIDAD_ENVIO = moment("2026-03-18", "YYYY-MM-DD").startOf("day");

// Pedidos por página. El backend topea en 50: cada pedido cuesta una llamada al PA
// de cabecera, así que páginas grandes se vuelven lentas.
const PEDIDOS_POR_PAGINA = 20;

// Mínimo de caracteres antes de buscar puntos de servicio (evita traer todo el padrón).
const MIN_LETRAS_BUSQUEDA_PS = 3;

// Jefatura/Gerencia de Operaciones no solicitan pedidos: ven el historial de su equipo,
// por eso se les muestra el solicitante.
const esCargoJefatura = (cargo?: string) => {
  const normalizado = String(cargo || "").trim().toUpperCase();
  return normalizado === "JEFE DE OPERACIONES" || normalizado === "GERENTE DE OPERACIONES";
};

const MisPedidos: React.FC = () => {
  const [pedidos, setPedidos] = useState<any[]>([]);
  const [present] = useIonToast();
  // ids de pedidos que estaban 'procesando' en el refresco anterior; sirve para
  // detectar la transicion procesando -> completo/parcial/error y avisar in-app.
  const prevProcesandoRef = useRef<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [cargo, setCargo] = useState("");
  // Filtro por mes/año + punto de servicio + estado.
  // Nota: enviamos fecha_desde/fecha_hasta al backend calculadas para el mes seleccionado.
  const [anio, setAnio] = useState<number>(moment().year());
  const [mes, setMes] = useState<number>(moment().month() + 1); // 1-12

  // Buscador de punto de servicio: el select no servía porque para jefatura/gerencia
  // el backend devuelve todos los PS.
  const [psBusqueda, setPsBusqueda] = useState<string>("");
  const [psResultados, setPsResultados] = useState<IfetchPuntoServicio[]>([]);
  const [psSeleccionado, setPsSeleccionado] = useState<string>(""); // ps_codigo
  const [psNombre, setPsNombre] = useState<string>("");
  const [psLoading, setPsLoading] = useState<boolean>(false);
  const [psError, setPsError] = useState<string | undefined>(undefined);

  const [estados, setEstados] = useState<IEstadoPedido[]>([]);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState<number | "">("");

  // Paginación
  const [pagina, setPagina] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [paginas, setPaginas] = useState<number>(1);

  const history = useHistory();

  const calcularRangoMes = useCallback((anioSel: number, mesSel: number) => {
    const inicio = moment({ year: anioSel, month: mesSel - 1, day: 1 })
      .startOf("month")
      .format("YYYY-MM-DD");
    const fin = moment({ year: anioSel, month: mesSel - 1, day: 1 })
      .endOf("month")
      .format("YYYY-MM-DD");
    return { inicio, fin };
  }, []);

  useEffect(() => {
    // Estados para el filtro; vienen de AE_PP_Estados.
    const cargarEstados = async () => {
      setEstados(await fetchEstadosPedido());
    };

    cargarEstados();
  }, []);

  useEffect(() => {
    // Búsqueda de PS con debounce. Si el texto es el del PS ya elegido, no se
    // vuelve a buscar (evita reabrir la lista al seleccionar).
    const texto = psBusqueda.trim();

    if (texto.length < MIN_LETRAS_BUSQUEDA_PS || texto === psNombre) {
      setPsResultados([]);
      setPsError(undefined);
      return;
    }

    const temporizador = setTimeout(async () => {
      setPsLoading(true);
      setPsError(undefined);
      try {
        const resultado = await fetchPuntoServicio(texto);
        if (!resultado.error) {
          setPsResultados(resultado.datos || []);
        } else {
          setPsResultados([]);
          setPsError(resultado.error);
        }
      } catch (error) {
        console.error("Error al buscar puntos de servicio:", error);
        setPsResultados([]);
        setPsError("Ocurrió un error al buscar puntos de servicio.");
      } finally {
        setPsLoading(false);
      }
    }, 400);

    return () => clearTimeout(temporizador);
  }, [psBusqueda, psNombre]);

  // La página se pasa por parámetro (y no se lee del estado) para que los botones
  // de paginación no dependan de un cierre desactualizado.
  const cargarDatos = useCallback(async (paginaPedida: number = 1, silent: boolean = false) => {
    if (!silent) setLoading(true);
    try {
      // Obtener cargo si no lo tenemos
      let cargoActual = cargo;
      if (!cargoActual) {
          const responseUser = await LoginAPI.me();
          const usuario = responseUser.data.datos;
          cargoActual = cargoParaPedido(usuario.emp_cargo);
          setCargo(cargoActual);
      }

      const { inicio, fin } = calcularRangoMes(anio, mes);

      // Obtener pedidos con filtros
      const data = await fetchMisPedidos({
        limite: PEDIDOS_POR_PAGINA,
        pagina: paginaPedida,
        fecha_desde: inicio,
        fecha_hasta: fin,
        ps: psSeleccionado || undefined,
        estado: estadoSeleccionado === "" ? undefined : Number(estadoSeleccionado),
      });

      if (data && data.datos) {
        // El backend ya excluye Iniciado/Cancelado salvo que se filtre por ese estado.
        const listaPedidos = data.datos || [];

        // Avisar in-app cuando un pedido que estaba 'procesando' termino su envio.
        const idPedido = (p: any) => Number(p.id || p.id_pc);
        const prev = prevProcesandoRef.current;
        listaPedidos.forEach((p: any) => {
          const id = idPedido(p);
          const envio = String(p?.envio_estado || "");
          if (prev.has(id) && envio && envio !== "procesando") {
            if (envio === "completo") {
              present({ color: "success", message: `El pedido #${id} se envió correctamente a Proactif.`, duration: 5000 });
            } else if (envio === "parcial") {
              present({ color: "warning", message: `El pedido #${id} se envió de forma parcial a Proactif.`, duration: 6000 });
            } else if (envio === "error") {
              present({ color: "danger", message: `No se pudo enviar el pedido #${id} a Proactif.`, duration: 6000 });
            }
          }
        });
        // Recalcular el set de 'procesando' actual para el proximo refresco.
        prevProcesandoRef.current = new Set(
          listaPedidos
            .filter((p: any) => String(p?.envio_estado || "") === "procesando")
            .map((p: any) => idPedido(p))
        );

        setPedidos(listaPedidos);
        setTotal(Number(data.total ?? listaPedidos.length));
        setPaginas(Math.max(1, Number(data.paginas ?? 1)));
        setPagina(Number(data.pagina ?? paginaPedida));
      } else {
          setPedidos([]);
          setTotal(0);
          setPaginas(1);
          prevProcesandoRef.current = new Set();
      }
    } catch (error) {
      console.error("Error al cargar mis pedidos:", error);
      setPedidos([]);
      setTotal(0);
      setPaginas(1);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [cargo, anio, mes, psSeleccionado, estadoSeleccionado, calcularRangoMes, present]);

  useEffect(() => {
    cargarDatos(1);
  }, []); // Cargar al inicio

  // Auto-refresco silencioso mientras haya algun pedido con envio 'procesando',
  // para que el badge y el aviso in-app se actualicen sin recargar a mano.
  const hayProcesando = pedidos.some((p: any) => String(p?.envio_estado || "") === "procesando");
  useEffect(() => {
    if (!hayProcesando) return;
    const intervalo = setInterval(() => { cargarDatos(pagina, true); }, 15000);
    return () => clearInterval(intervalo);
  }, [hayProcesando, pagina, cargarDatos]);

  const seleccionarPs = (ps: IfetchPuntoServicio) => {
    setPsSeleccionado(ps.ps_codigo);
    setPsNombre(ps.ps_nombre);
    setPsBusqueda(ps.ps_nombre);
    setPsResultados([]);
  };

  const limpiarPs = () => {
    setPsSeleccionado("");
    setPsNombre("");
    setPsBusqueda("");
    setPsResultados([]);
  };

  const irAPagina = (destino: number) => {
    const siguiente = Math.min(Math.max(1, destino), paginas);
    if (siguiente === pagina) return;
    cargarDatos(siguiente);
  };

  const handleVerPedido = async (pedido: any) => {
    try {
      const idPC = pedido.id || pedido.id_pc;
      const detalleCab = await fetchCabeceraDetalle(idPC, true);

      // Navegar al detalle con la estructura que espera y flag readonly
      history.push("/pedido-de-productos-detalles", {
        datos: detalleCab?.datos || detalleCab,
        id_pc: Number(detalleCab?.datos?.cabecera?.id || detalleCab?.id || idPC),
        tolerado: Number(detalleCab?.datos?.cabecera?.tolerado || detalleCab?.tolerado || 0),
        facturado: Number(detalleCab?.datos?.cabecera?.facturar || detalleCab?.facturado || 0),
        facturarParams: Boolean(detalleCab?.datos?.cabecera?.facturar || detalleCab?.facturado),
        cargo: cargo,
        readonly: true // Flag para modo solo lectura
      });
    } catch (error) {
      console.error("Error al obtener detalle del pedido:", error);
    }
  };

  // TD-11079: el estado sigue llamandose "Aprobación Gerencia Gentis" en la BD;
  // al usuario se le muestra el nombre nuevo del area que aprueba.
  const nombreEstado = (estado: string) =>
    estado === "Aprobación Gerencia Gentis"
      ? "Aprobación Gerencia Unidades Especializadas"
      : estado;

  const getBadgeColor = (estado: string) => {
    switch (estado) {
      case "Iniciado": return "medium";
      case "En curso": return "primary";
      case "Aprobación Jefatura": return "warning";
      case "Aprobación Gerencia": return "danger";
      case "Aprobación Gerencia Gentis": return "danger";
      case "Pedido realizado": return "success";
      case "Entregado/Finalizado": return "success";
      case "Rechazado": return "danger";
      case "Cancelado": return "medium";
      default: return "light";
    }
  };

  // Indicador del envío a Proactif (corre en segundo plano tras aceptar el pedido).
  // Reutiliza envio_estado de la cabecera; 'completo' no muestra chip (el estado
  // "En curso" + Enviados N/N ya lo comunica).
  const renderEnvioBadge = (pedido: any) => {
    const envio = String(pedido?.envio_estado || "");
    if (envio === "procesando") {
      return (
        <IonBadge color="primary" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <IonSpinner name="dots" style={{ width: 14, height: 14 }} />
          Enviando…
        </IonBadge>
      );
    }
    if (envio === "parcial") {
      return <IonBadge color="warning">Envío parcial</IonBadge>;
    }
    if (envio === "error") {
      return <IonBadge color="danger">Error de envío</IonBadge>;
    }
    return null;
  };

  const mostrarTrazabilidadEnvio = (fechaPedido: any) => {
    const fecha = moment(fechaPedido);
    return fecha.isValid() && fecha.isSameOrAfter(FECHA_CORTE_TRAZABILIDAD_ENVIO);
  };

  const esJefatura = esCargoJefatura(cargo);

  return (
    <IonPage>
      <Header titulo={esJefatura ? "Historial de Pedidos" : "Mis Pedidos"}>
        <IonToolbar>
            <IonTitle>{esJefatura ? "Historial de Pedidos del Equipo" : "Historial de Pedidos"}</IonTitle>
        </IonToolbar>
      </Header>
      <IonContent>
        <IonCard>
            <IonCardContent>
                <IonGrid>
                    <IonRow>
                        <IonCol size="6">
                            <IonItem lines="none">
                                <IonLabel position="stacked">Mes</IonLabel>
                                <IonSelect
                                  interface="alert"
                                  okText="Aceptar"
                                  cancelText="Cancelar"
                                  interfaceOptions={{ header: "Seleccionar mes" }}
                                  value={mes}
                                  onIonChange={(e) => setMes(Number(e.detail.value))}
                                >
                                  <IonSelectOption value={1}>Enero</IonSelectOption>
                                  <IonSelectOption value={2}>Febrero</IonSelectOption>
                                  <IonSelectOption value={3}>Marzo</IonSelectOption>
                                  <IonSelectOption value={4}>Abril</IonSelectOption>
                                  <IonSelectOption value={5}>Mayo</IonSelectOption>
                                  <IonSelectOption value={6}>Junio</IonSelectOption>
                                  <IonSelectOption value={7}>Julio</IonSelectOption>
                                  <IonSelectOption value={8}>Agosto</IonSelectOption>
                                  <IonSelectOption value={9}>Septiembre</IonSelectOption>
                                  <IonSelectOption value={10}>Octubre</IonSelectOption>
                                  <IonSelectOption value={11}>Noviembre</IonSelectOption>
                                  <IonSelectOption value={12}>Diciembre</IonSelectOption>
                                </IonSelect>
                            </IonItem>
                        </IonCol>
                        <IonCol size="6">
                            <IonItem lines="none">
                                <IonLabel position="stacked">Año</IonLabel>
                                <IonSelect
                                  interface="alert"
                                  okText="Aceptar"
                                  cancelText="Cancelar"
                                  interfaceOptions={{ header: "Seleccionar año" }}
                                  value={anio}
                                  onIonChange={(e) => setAnio(Number(e.detail.value))}
                                >
                                  <IonSelectOption value={moment().year()}>{moment().year()}</IonSelectOption>
                                  <IonSelectOption value={moment().year() - 1}>{moment().year() - 1}</IonSelectOption>
                                  <IonSelectOption value={moment().year() - 2}>{moment().year() - 2}</IonSelectOption>
                                  <IonSelectOption value={moment().year() - 3}>{moment().year() - 3}</IonSelectOption>
                                  <IonSelectOption value={moment().year() - 4}>{moment().year() - 4}</IonSelectOption>
                                </IonSelect>
                            </IonItem>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                      <IonCol size="12">
                        <IonItem lines="none">
                          <IonLabel position="stacked">Estado</IonLabel>
                          <IonSelect
                            interface="alert"
                            okText="Aceptar"
                            cancelText="Cancelar"
                            interfaceOptions={{ header: "Seleccionar estado" }}
                            value={estadoSeleccionado}
                            onIonChange={(e) => {
                              const valor = e.detail.value;
                              setEstadoSeleccionado(valor === "" || valor == null ? "" : Number(valor));
                            }}
                          >
                            <IonSelectOption value="">Todos</IonSelectOption>
                            {estados.map((estado) => (
                              <IonSelectOption key={estado.id} value={estado.id}>
                                {nombreEstado(estado.estado)}
                              </IonSelectOption>
                            ))}
                          </IonSelect>
                        </IonItem>
                      </IonCol>
                    </IonRow>
                    <IonRow>
                      <IonCol size="12">
                        <IonItem lines="none">
                          <IonLabel position="stacked">Punto de servicio</IonLabel>
                          <IonInput
                            value={psBusqueda}
                            clearInput={true}
                            placeholder={`Buscar por nombre (mínimo ${MIN_LETRAS_BUSQUEDA_PS} letras)`}
                            onIonChange={(e) => setPsBusqueda(e.detail.value ?? "")}
                          />
                          <IonIcon slot="end" icon={search} />
                        </IonItem>

                        {psSeleccionado && (
                          <IonChip color="primary" onClick={limpiarPs}>
                            <IonLabel>{psNombre || psSeleccionado}</IonLabel>
                            <IonIcon icon={closeCircle} />
                          </IonChip>
                        )}

                        {psLoading && (
                          <div style={{ padding: "6px 0" }}>
                            <IonSpinner name="dots" />
                          </div>
                        )}

                        {psResultados.length > 0 && (
                          <IonList style={{ maxHeight: 220, overflowY: "auto" }}>
                            {psResultados.map((ps) => (
                              <IonItem button key={ps.ps_codigo} onClick={() => seleccionarPs(ps)}>
                                <IonLabel>
                                  {ps.ps_nombre}
                                  <p>{ps.ps_codigo}</p>
                                </IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        )}

                        {psError && (
                          <div style={{ marginTop: "6px" }}>
                            <IonText color="danger">{psError}</IonText>
                          </div>
                        )}
                      </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol>
                            <IonButton expand="block" onClick={() => cargarDatos(1)} disabled={loading}>
                                <IonIcon slot="start" icon={search} />
                                Filtrar
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </IonGrid>
            </IonCardContent>
        </IonCard>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <IonSpinner />
          </div>
        ) : pedidos.length === 0 ? (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <IonText>No se encontraron pedidos en este rango.</IonText>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center", marginTop: "8px" }}>
              <IonText color="medium">
                {total} pedido{total === 1 ? "" : "s"} · página {pagina} de {paginas}
              </IonText>
            </div>
            <IonList>
              {pedidos.map((pedido, index) => (
                <IonCard key={index}>
                  <IonCardHeader>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <IonCardTitle style={{ fontSize: '1.1em' }}>Pedido #{pedido.id || pedido.id_pc}</IonCardTitle>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {renderEnvioBadge(pedido)}
                            <IonBadge color={getBadgeColor(pedido.estado)}>{nombreEstado(pedido.estado)}</IonBadge>
                          </div>
                      </div>
                  </IonCardHeader>
                  <IonCardContent>
                    <p><strong>Punto de Servicio:</strong> {pedido.ps_nombre || pedido.ps_objeto}</p>
                    {esJefatura && (
                      <p><strong>Solicitante:</strong> {pedido.fiscal || pedido.fis_cedula || 'Sin identificar'}</p>
                    )}
                    <p><strong>Fecha:</strong> {moment(pedido.fecha).format('DD/MM/YYYY HH:mm')}</p>
                    {pedido.codigo_referencia && (
                      <p><strong>PV:</strong> {pedido.codigo_referencia}</p>
                    )}
                    {mostrarTrazabilidadEnvio(pedido.fecha) && (
                      <p>
                        <strong>Enviados:</strong>{' '}
                        {(Number(pedido.items_total || 0) > 0)
                          ? `${Number(pedido.items_enviados || 0)} / ${Number(pedido.items_total || 0)}`
                          : '0 / 0'}
                      </p>
                    )}
                    {pedido.excedente > 0 && (
                        <p style={{ color: 'var(--ion-color-danger)' }}><strong>Excedente:</strong> {pedido.excedente}%</p>
                    )}
                    <div style={{ marginTop: '10px', textAlign: 'right' }}>
                      <IonButton size="small" fill="outline" onClick={() => handleVerPedido(pedido)}>
                        <IonIcon slot="start" icon={eye} />
                        Ver Detalle
                      </IonButton>
                    </div>
                  </IonCardContent>
                </IonCard>
              ))}
            </IonList>

            {paginas > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '4px 0 16px 0',
                }}
              >
                <IonButton size="small" fill="outline" disabled={pagina <= 1} onClick={() => irAPagina(pagina - 1)}>
                  <IonIcon slot="start" icon={chevronBack} />
                  Anterior
                </IonButton>
                <IonText>{pagina} / {paginas}</IonText>
                <IonButton size="small" fill="outline" disabled={pagina >= paginas} onClick={() => irAPagina(pagina + 1)}>
                  Siguiente
                  <IonIcon slot="end" icon={chevronForward} />
                </IonButton>
              </div>
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default MisPedidos;
